import React, { useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  required,
  minValue,
} from 'react-admin';
import { Typography, Alert, Box, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';

const IconAssetsInfoBox: React.FC = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Alert
      severity="info"
      icon={<InfoIcon />}
      sx={{ mb: 2 }}
      action={
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      }
    >
      <Typography variant="subtitle2" fontWeight="bold">
        Como configurar ícones alternativos do app
      </Typography>
      <Collapse in={expanded}>
        <Box sx={{ mt: 2, fontSize: '0.875rem' }}>
          <Typography variant="body2" paragraph>
            Para que o ícone alternativo funcione, é necessário criar os assets no repositório
            <strong> wl-src/&#123;rede&#125;/</strong> antes de rodar o wl-script.
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
            Estrutura de pastas necessária:
          </Typography>

          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              overflow: 'auto',
              my: 1,
            }}
          >
{`wl-src/{rede}/
├── icons/
│   ├── ios/
│   │   └── Assets.xcassets/
│   │       └── AppIcon{Nome}.appiconset/  ← Ex: AppIconPro.appiconset
│   │           ├── Contents.json
│   │           └── *.png (todas as resoluções)
│   └── android/
│       └── res/
│           ├── mipmap-mdpi/
│           │   └── ic_launcher_{nome}.png  ← Ex: ic_launcher_pro.png
│           ├── mipmap-hdpi/
│           ├── mipmap-xhdpi/
│           ├── mipmap-xxhdpi/
│           └── mipmap-xxxhdpi/
└── icon-preview-{nome}.png  ← Preview 160x160 para exibir no app`}
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
            Importante:
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 2, '& li': { mb: 0.5 } }}>
            <li>
              <Typography variant="body2">
                O <strong>nome do ícone</strong> deve ser em minúsculo e sem espaços (ex: pro, gold, vip, premium)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                iOS: A pasta deve ser <code>AppIcon&#123;Nome&#125;.appiconset</code> (ex: AppIconPro.appiconset)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Android: O arquivo deve ser <code>ic_launcher_&#123;nome&#125;.png</code> (ex: ic_launcher_pro.png)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                O preview <code>icon-preview-&#123;nome&#125;.png</code> é exibido no modal de celebração do app
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Use &quot;default&quot; se não quiser ícone alternativo (ícone padrão da rede)
              </Typography>
            </li>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2 }}>
            Após criar os assets:
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              my: 1,
            }}
          >
            npm run wl-script &#123;rede&#125;
          </Box>
          <Typography variant="body2" color="textSecondary">
            O script detecta automaticamente os ícones e configura o app.
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  );
};

const PlanCreate: React.FC = () => {
  const [chainId] = useState(getChainId());

  if (!chainId) {
    return <Typography>Selecione uma Rede</Typography>;
  }

  const transform = (data: any) => ({
    ...data,
    chainId,
  });

  return (
    <Permission permission={PERMISSIONS.PLAN}>
      <Create title="Cadastrar Plano" transform={transform} redirect="list">
        <SimpleForm sx={{ maxWidth: 600 }}>
          <IconAssetsInfoBox />

          <TextInput
            source="name"
            label="Nome do Plano"
            validate={required('Nome é obrigatório')}
            fullWidth
          />
          <TextInput
            source="description"
            label="Descrição"
            multiline
            rows={3}
            fullWidth
          />
          <TextInput
            source="iconName"
            label="Nome do Ícone"
            defaultValue="default"
            validate={required('Nome do ícone é obrigatório')}
            fullWidth
            helperText="Identificador do ícone alternativo (ex: pro, gold, vip). Use 'default' para ícone padrão."
          />
          <NumberInput
            source="priority"
            label="Prioridade"
            defaultValue={1}
            validate={[required('Prioridade é obrigatória'), minValue(1, 'Mínimo é 1')]}
            fullWidth
            helperText="Menor número = maior prioridade. Quando usuário tem múltiplos planos, o de menor número prevalece."
          />
          <NumberInput
            source="durationDays"
            label="Duração (dias)"
            defaultValue={30}
            validate={[required('Duração é obrigatória'), minValue(1, 'Mínimo é 1 dia')]}
            fullWidth
            helperText="Quantidade de dias que o plano fica ativo após o usuário entrar"
          />
          <BooleanInput source="isEnabled" label="Ativo" defaultValue={true} />
        </SimpleForm>
      </Create>
    </Permission>
  );
};

export default PlanCreate;
